import React from 'react';

const TemplateResultView = ({
  studentData,
  schoolName,
  academicYear,
  classValue,
  division,
  language,
  subjectSequence = [],
  attendance = { Present: {}, Absent: {}, Leave: {} },
  schoolLogo,
  udiseNumber
}) => {
  const student = studentData || {};
  const results = student?.results || student?.allResults || {};

  // Helper to extract numeric value cleanly
  const getNum = (val) => {
    if (val === undefined || val === null || val === '' || val === '-') return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
  };

  // 1. Build dynamic list of all subjects
  const resultKeys = Object.keys(results);
  const rawSubjectList = subjectSequence && subjectSequence.length > 0
    ? [...subjectSequence, ...resultKeys.filter(k => !subjectSequence.includes(k))]
    : (resultKeys.length > 0 ? resultKeys : [
        "English", "Sanskrit (E)", "Hindi (C)", "Sanskrit (C)",
        "Marathi", "Maths 1", "Maths 2", "Sci.&Tech. I", "Sci.&Tech. II",
        "History & P. Sci.", "Geography"
      ]);

  // Clean & unique subjects
  const uniqueSubjects = [];
  rawSubjectList.forEach(s => {
    if (s && typeof s === 'string' && s.trim() !== "" && !uniqueSubjects.includes(s.trim())) {
      uniqueSubjects.push(s.trim());
    }
  });

  // 2. Classify Graded vs Written Subjects
  const gradedKeywords = [
    "jalsuraksha", "arogya", "sanrakshan", "water security", "phy. edu",
    "physical education", "defence", "computer", "sanganak", "à¤¸à¤‚à¤—à¤£à¤•",
    "à¤œà¤²à¤¸à¥à¤°à¤•à¥à¤·à¤¾", "à¤†à¤°à¥‹à¤—à¥à¤¯", "à¤¸à¤‚à¤°à¤•à¥à¤·à¤£", "à¤•à¤¾à¤°à¥à¤¯à¤¾à¤¨à¥à¤­à¤µ", "à¤•à¤²à¤¾", "art", "work exp",
    "drawing", "craft", "music"
  ];

  const isGradedSubject = (subjectName) => {
    const sLower = subjectName.toLowerCase();
    if (gradedKeywords.some(k => sLower.includes(k))) return true;
    const actualKey = Object.keys(results).find(k => k.trim().toLowerCase() === subjectName.trim().toLowerCase()) || subjectName;
    const res = results[actualKey];
    if (res && res.grade && !res.obtainMarks && !res.writtenMarks && !res.theoryMarks && !res.total && !res.firstSemesterTotal) {
      return true;
    }
    return false;
  };

  const writtenSubjects = uniqueSubjects.filter(s => !isGradedSubject(s));
  const gradedSubjectsList = uniqueSubjects.filter(s => isGradedSubject(s));

  // 3. Extract metrics for each written subject
  const getSubjectMetrics = (subjectName) => {
    const actualKey = Object.keys(results).find(k => k.trim().toLowerCase() === subjectName.trim().toLowerCase()) || subjectName;
    const res = results[actualKey] || {};

    const ut1 = res["Unit Test I"] || res["Unit Test 1"] || res.ut1 || {};
    const ut2 = res["Unit Test II"] || res["Unit Test 2"] || res.ut2 || {};
    const ut3 = res["Unit Test III"] || res["Unit Test 3"] || res.ut3 || {};
    const ut4 = res["Unit Test IV"] || res["Unit Test 4"] || res.ut4 || {};

    const sem1 = res["Semester First "] || res["Semester First"] || res["First Semester"] || res.firstSemesterMarks || res.sem1 || {};
    const sem2 = res["Semester Second "] || res["Semester Second"] || res["Second Semester"] || res.secondSemesterMarks || res.sem2 || {};

    // UT 1+2
    const ut1_m = getNum(ut1.obtainMarks);
    const ut2_m = getNum(ut2.obtainMarks);
    const ut1_2_m = (ut1_m !== null || ut2_m !== null) ? (ut1_m || 0) + (ut2_m || 0) : null;
    const ut1_2_out = (getNum(ut1.outOf) || 10) + (getNum(ut2.outOf) || 10);

    // UT 3+4
    const ut3_m = getNum(ut3.obtainMarks);
    const ut4_m = getNum(ut4.obtainMarks);
    const ut3_4_m = (ut3_m !== null || ut4_m !== null) ? (ut3_m || 0) + (ut4_m || 0) : null;
    const ut3_4_out = (getNum(ut3.outOf) || 10) + (getNum(ut4.outOf) || 10);

    // Sem 1
    const sem1_int_m = getNum(sem1.oralMarks) ?? getNum(sem1.internalMarks) ?? getNum(sem1.internal) ?? null;
    const sem1_int_out = getNum(sem1.oralOutOf) ?? getNum(sem1.internalOutOf) ?? 20;

    const sem1_th_m = getNum(sem1.writtenMarks) ?? getNum(sem1.theoryMarks) ?? getNum(sem1.theory) ?? null;
    const sem1_th_out = getNum(sem1.writtenOutOf) ?? getNum(sem1.theoryOutOf) ?? 80;

    const sem1_tot_m = getNum(sem1.obtainMarks) ?? getNum(sem1.total) ?? (sem1_int_m !== null || sem1_th_m !== null ? (sem1_int_m || 0) + (sem1_th_m || 0) : res.firstSemesterTotal ?? null);
    const sem1_tot_out = getNum(sem1.outOf) ?? (sem1_int_out + sem1_th_out);

    // Sem 2
    const sem2_int_m = getNum(sem2.oralMarks) ?? getNum(sem2.internalMarks) ?? getNum(sem2.internal) ?? null;
    const sem2_int_out = getNum(sem2.oralOutOf) ?? getNum(sem2.internalOutOf) ?? 20;

    const sem2_th_m = getNum(sem2.writtenMarks) ?? getNum(sem2.theoryMarks) ?? getNum(sem2.theory) ?? null;
    const sem2_th_out = getNum(sem2.writtenOutOf) ?? getNum(sem2.theoryOutOf) ?? 80;

    const sem2_tot_m = getNum(sem2.obtainMarks) ?? getNum(sem2.total) ?? (sem2_int_m !== null || sem2_th_m !== null ? (sem2_int_m || 0) + (sem2_th_m || 0) : res.total ?? null);
    const sem2_tot_out = getNum(sem2.outOf) ?? (sem2_int_out + sem2_th_out);

    // Totals
    const hasAB = sem1_tot_m !== null || sem2_tot_m !== null;
    const tot_AB = hasAB ? (sem1_tot_m || 0) + (sem2_tot_m || 0) : null;
    const ab_div_2 = hasAB ? Math.round(tot_AB / 2) : null;
    const grace = getNum(res.grace) || getNum(sem2.grace) || null;
    const final_100 = ab_div_2 !== null ? ab_div_2 + (grace || 0) : null;

    return {
      name: subjectName,
      ut1_2_m, ut1_2_out,
      ut3_4_m, ut3_4_out,
      sem1_int_m, sem1_int_out,
      sem1_th_m, sem1_th_out,
      sem1_tot_m, sem1_tot_out,
      sem2_int_m, sem2_int_out,
      sem2_th_m, sem2_th_out,
      sem2_tot_m, sem2_tot_out,
      tot_AB, ab_div_2, grace, final_100
    };
  };

  let computedWrittenRows = writtenSubjects.map(s => getSubjectMetrics(s));

  // Merge Maths and Science subjects as per requirement
  const mergeSubjectRows = (rows, name1Variants, name2Variants, newName) => {
    const idx1 = rows.findIndex(r => name1Variants.some(v => r.name.toLowerCase().includes(v)));
    const idx2 = rows.findIndex(r => name2Variants.some(v => r.name.toLowerCase().includes(v)));

    if (idx1 !== -1 && idx2 !== -1 && idx1 !== idx2) {
      const r1 = rows[idx1];
      const r2 = rows[idx2];
      const add = (a, b) => (a === null && b === null) ? null : (a || 0) + (b || 0);

      const merged = {
        name: newName,
        ut1_2_m: add(r1.ut1_2_m, r2.ut1_2_m), ut1_2_out: (r1.ut1_2_out || 0) + (r2.ut1_2_out || 0),
        ut3_4_m: add(r1.ut3_4_m, r2.ut3_4_m), ut3_4_out: (r1.ut3_4_out || 0) + (r2.ut3_4_out || 0),
        sem1_int_m: add(r1.sem1_int_m, r2.sem1_int_m), sem1_int_out: (r1.sem1_int_out || 0) + (r2.sem1_int_out || 0),
        sem1_th_m: add(r1.sem1_th_m, r2.sem1_th_m), sem1_th_out: (r1.sem1_th_out || 0) + (r2.sem1_th_out || 0),
        sem1_tot_m: add(r1.sem1_tot_m, r2.sem1_tot_m), sem1_tot_out: (r1.sem1_tot_out || 0) + (r2.sem1_tot_out || 0),
        sem2_int_m: add(r1.sem2_int_m, r2.sem2_int_m), sem2_int_out: (r1.sem2_int_out || 0) + (r2.sem2_int_out || 0),
        sem2_th_m: add(r1.sem2_th_m, r2.sem2_th_m), sem2_th_out: (r1.sem2_th_out || 0) + (r2.sem2_th_out || 0),
        sem2_tot_m: add(r1.sem2_tot_m, r2.sem2_tot_m), sem2_tot_out: (r1.sem2_tot_out || 0) + (r2.sem2_tot_out || 0),
        tot_AB: add(r1.tot_AB, r2.tot_AB), ab_div_2: add(r1.ab_div_2, r2.ab_div_2),
        grace: add(r1.grace, r2.grace), final_100: add(r1.final_100, r2.final_100)
      };
      
      const minIdx = Math.min(idx1, idx2);
      const maxIdx = Math.max(idx1, idx2);
      rows[minIdx] = merged;
      rows.splice(maxIdx, 1);
    } else if (idx1 !== -1) {
      rows[idx1].name = newName;
    }
  };

  mergeSubjectRows(computedWrittenRows, ["maths 1", "maths-i", "maths i", "maths-1"], ["maths 2", "maths-ii", "maths ii", "maths-2"], "Maths - I + II");
  mergeSubjectRows(computedWrittenRows, ["sci.&tech. i", "sci. & tech.-i", "sci & tech - i", "science 1"], ["sci.&tech. ii", "sci. & tech.-ii", "sci & tech - ii", "science 2"], "Sci & Tech - I + II");

  // Calculate Grand Total across all written subjects
  let grand_ut1_2_m = 0, grand_ut1_2_out = 0;
  let grand_ut3_4_m = 0, grand_ut3_4_out = 0;
  let grand_sem1_int_m = 0, grand_sem1_int_out = 0;
  let grand_sem1_th_m = 0, grand_sem1_th_out = 0;
  let grand_sem1_tot_m = 0, grand_sem1_tot_out = 0;
  let grand_sem2_int_m = 0, grand_sem2_int_out = 0;
  let grand_sem2_th_m = 0, grand_sem2_th_out = 0;
  let grand_sem2_tot_m = 0, grand_sem2_tot_out = 0;
  let grand_tot_AB = 0, grand_ab_div_2 = 0, grand_grace = 0, grand_final_100 = 0;

  computedWrittenRows.forEach(r => {
    if (r.ut1_2_m !== null) grand_ut1_2_m += r.ut1_2_m;
    grand_ut1_2_out += r.ut1_2_out;

    if (r.ut3_4_m !== null) grand_ut3_4_m += r.ut3_4_m;
    grand_ut3_4_out += r.ut3_4_out;

    if (r.sem1_int_m !== null) grand_sem1_int_m += r.sem1_int_m;
    grand_sem1_int_out += r.sem1_int_out;

    if (r.sem1_th_m !== null) grand_sem1_th_m += r.sem1_th_m;
    grand_sem1_th_out += r.sem1_th_out;

    if (r.sem1_tot_m !== null) grand_sem1_tot_m += r.sem1_tot_m;
    grand_sem1_tot_out += r.sem1_tot_out;

    if (r.sem2_int_m !== null) grand_sem2_int_m += r.sem2_int_m;
    grand_sem2_int_out += r.sem2_int_out;

    if (r.sem2_th_m !== null) grand_sem2_th_m += r.sem2_th_m;
    grand_sem2_th_out += r.sem2_th_out;

    if (r.sem2_tot_m !== null) grand_sem2_tot_m += r.sem2_tot_m;
    grand_sem2_tot_out += r.sem2_tot_out;

    if (r.tot_AB !== null) grand_tot_AB += r.tot_AB;
    if (r.ab_div_2 !== null) grand_ab_div_2 += r.ab_div_2;
    if (r.grace !== null) grand_grace += r.grace;
    if (r.final_100 !== null) grand_final_100 += r.final_100;
  });

  // Calculate Overall Percentage and Grade
  const grandObtained = grand_final_100 || grand_sem2_tot_m || grand_sem1_tot_m || 0;
  const grandOutOf = (computedWrittenRows.length * 100) || 600;
  const overallPercentage = grandObtained > 0 ? ((grandObtained / grandOutOf) * 100).toFixed(2) : "0.00";

  const getGradeFromPct = (pct) => {
    const p = parseFloat(pct);
    if (p >= 75) return "A";
    if (p >= 60) return "B";
    if (p >= 50) return "C";
    if (p >= 35) return "D";
    return "E";
  };

  const getGradeDesc = (gr) => {
    switch (gr) {
      case "A": return "Excellent";
      case "B": return "Very Good";
      case "C": return "Good";
      case "D": return "Satisfactory";
      case "E": return "Not Satisfactory";
      default: return "-";
    }
  };

  const overallGrade = getGradeFromPct(overallPercentage);

  // Fallback / standard grade subjects list to display on bottom left
  const displayGradeSubjects = [...gradedSubjectsList];
  const defaultGradeDefaults = ["Defence Studies", "Water Security", "Phy. Edu.", "Computer"];
  defaultGradeDefaults.forEach(defSub => {
    if (displayGradeSubjects.length < 4 && !displayGradeSubjects.includes(defSub)) {
      displayGradeSubjects.push(defSub);
    }
  });

  const getGradeValueForSubject = (name) => {
    const actualKey = Object.keys(results).find(k => k.trim().toLowerCase() === name.trim().toLowerCase()) || name;
    const res = results[actualKey];
    if (res) {
      const val = res.grade || res.obtainMarks || res?.["Second Semester"]?.grade || res?.["First Semester"]?.grade;
      if (val) return val;
    }
    // Partial search
    const matchedKey = resultKeys.find(k => k.toLowerCase().includes(name.toLowerCase()));
    if (matchedKey && results[matchedKey]) {
      const r = results[matchedKey];
      return r.grade || r.obtainMarks || "A";
    }
    return "A";
  };

  const getPromotedClass = (cls) => {
    if (!cls) return "Std. X";
    if (cls.includes("IX") || cls.includes("9")) return "Std. X";
    if (cls.includes("VIII") || cls.includes("8")) return "Std. IX";
    return "Std. X";
  };

  return (
    <>
      <div className="template-result-sheet-container" style={{ 
        pageBreakAfter: 'always',
        width: '100%',
        maxWidth: '280mm',
        margin: '0 auto 40px auto',
        backgroundColor: '#fff',
        padding: '25px',
        boxSizing: 'border-box',
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: '11px',
        color: '#000',
        border: '2px solid #000',
        minHeight: '190mm',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1, padding: '15px' }}>
          
          {/* Right Side: Attendance */}
          <div style={{ width: '48%', border: '2px solid black', padding: '15px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold' }}>{language === 'English' ? 'Attendance:' : 'à¤¹à¤œà¥‡à¤°à¥€'}</h2>
            
            {/* First Term Attendance Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', marginBottom: '15px' }} className="attendance-table">
              <thead>
                <tr>
                  <th style={{ border: '1px solid black', padding: '4px' }}>{language === 'English' ? 'Month' : 'à¤®à¤¹à¤¿à¤¨à¤¾'}</th>
                  {['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'].map(m => <th key={m} style={{ border: '1px solid black', padding: '4px' }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {['Present', 'Absent'].map(type => (
                  <tr key={type}>
                    <td style={{ border: '1px solid black', padding: '4px' }}>{type === 'Present' ? (language === 'English' ? 'Present Days' : 'à¤¹à¤œà¤° à¤¦à¤¿à¤µà¤¸') : (language === 'English' ? 'Working Days' : 'à¤•à¤¾à¤®à¤¾à¤šà¥‡ à¤¦à¤¿à¤µà¤¸')}</td>
                    {['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'].map(m => (
                      <td key={m} style={{ border: '1px solid black', padding: '4px' }}>
                        {attendance?.[type]?.[m] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Second Term Attendance Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }} className="attendance-table">
              <thead>
                <tr>
                  <th style={{ border: '1px solid black', padding: '4px' }}>{language === 'English' ? 'Month' : 'à¤®à¤¹à¤¿à¤¨à¤¾'}</th>
                  {['Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'Total'].map(m => <th key={m} style={{ border: '1px solid black', padding: '4px' }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {['Present', 'Absent'].map(type => (
                  <tr key={type}>
                    <td style={{ border: '1px solid black', padding: '4px' }}>{type === 'Present' ? (language === 'English' ? 'Present Days' : 'à¤¹à¤œà¤° à¤¦à¤¿à¤µà¤¸') : (language === 'English' ? 'Working Days' : 'à¤•à¤¾à¤®à¤¾à¤šà¥‡ à¤¦à¤¿à¤µà¤¸')}</td>
                    {['Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'Total'].map(m => {
                      let cellVal = '';
                      if (m === 'Total') {
                        let sum = 0; let hasAny = false;
                        ['Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr'].forEach(month => {
                          const v = attendance?.[type]?.[month];
                          if (v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) {
                            sum += Number(v); hasAny = true;
                          }
                        });
                        cellVal = hasAny ? sum : '';
                      } else {
                        cellVal = attendance?.[type]?.[m] || '';
                      }
                      return (
                        <td key={m} style={{ border: '1px solid black', padding: '4px' }}>{cellVal}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '20px' }}>
              <p style={{ margin: '5px 0', fontWeight: 'bold' }}>{language === 'English' ? 'The school will reopen on' : 'à¤¶à¤¾à¤³à¤¾ à¤ªà¥à¤¨à¥à¤¹à¤¾ à¤¸à¥à¤°à¥‚ à¤¹à¥‹à¤£à¥à¤¯à¤¾à¤šà¥€ à¤¤à¤¾à¤°à¥€à¤– :'}</p>
              <p style={{ margin: '15px 0 40px 0', fontWeight: 'bold', fontSize: '13px' }}>
                {language === 'English' ? 'Date: _________ at _________ am/pm' : 'à¤¦à¤¿à¤¨à¤¾à¤‚à¤•: _________ à¤µà¥‡à¤³ _________'}
              </p>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '60px' }}>
                <strong>{language === 'English' ? "Class Teacher's Signature" : 'à¤µà¤°à¥à¤—à¤¶à¤¿à¤•à¥à¤·à¤•à¤¾à¤šà¥€ à¤¸à¤¹à¥€'}</strong>
                <strong>{language === 'English' ? 'Parents Signature' : 'à¤ªà¤¾à¤²à¤•à¤¾à¤‚à¤šà¥€ à¤¸à¤¹à¥€'}</strong>
              </div>
            </div>

            <div style={{ marginTop: '30px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{language === 'English' ? 'Instructions for parents:' : 'à¤ªà¤¾à¤²à¤•à¤¾à¤‚à¤¸à¤¾à¤ à¥€ à¤¸à¥‚à¤šà¤¨à¤¾:'}</p>
              <ol style={{ paddingLeft: '20px', margin: 0, fontSize: '11px' }}>
                <li>{language === 'English' ? 'Promotions are strictly made following the rules laid by the department of education.' : 'à¤¶à¤¿à¤•à¥à¤·à¤£ à¤µà¤¿à¤­à¤¾à¤—à¤¾à¤šà¥à¤¯à¤¾ à¤¨à¤¿à¤¯à¤®à¤¾à¤‚à¤¨à¥à¤¸à¤¾à¤°à¤š à¤ªà¥à¤¢à¥€à¤² à¤µà¤°à¥à¤—à¤¾à¤¤ à¤ªà¤¦à¥‹à¤¨à¥à¤¨à¤¤à¥€ à¤¦à¤¿à¤²à¥€ à¤œà¤¾à¤ˆà¤².'}</li>
                <li>{language === 'English' ? 'Result will not be declared, unless & until school fees dues are paid.' : 'à¤¶à¤¾à¤³à¥‡à¤šà¥€ à¤¸à¤‚à¤ªà¥‚à¤°à¥à¤£ à¤«à¥€ à¤­à¤°à¤²à¥à¤¯à¤¾à¤¶à¤¿à¤µà¤¾à¤¯ à¤¨à¤¿à¤•à¤¾à¤² à¤œà¤¾à¤¹à¥€à¤° à¤•à¥‡à¤²à¤¾ à¤œà¤¾à¤£à¤¾à¤° à¤¨à¤¾à¤¹à¥€.'}</li>
              </ol>
            </div>
          </div>
          {/* Left Side: Progress Card */}
          <div style={{ width: '48%', border: '2px solid black', padding: '15px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ border: '1px solid black', padding: '10px', textAlign: 'center', marginBottom: '15px', borderRadius: '10px', backgroundColor: '#f9f9f9' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0' }}>{schoolName}</h2>
              {schoolLogo && <img src={schoolLogo} alt="Logo" style={{ width: '60px', height: '60px', objectFit: 'contain', margin: '10px 0' }} />}
              <h3 style={{ margin: '5px 0', fontSize: '16px', fontWeight: 'bold' }}>{language === 'English' ? 'PROGRESS CARD' : 'à¤ªà¥à¤°à¤—à¤¤à¥€ à¤ªà¤¤à¥à¤°à¤•'}</h3>
              <p style={{ margin: '0', fontSize: '12px', fontWeight: 'bold' }}>{language === 'English' ? 'Year -' : 'à¤µà¤°à¥à¤· -'} {academicYear}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', border: '1px solid black', padding: '15px', borderRadius: '10px', backgroundColor: '#f9f9f9', fontSize: '12px' }}>
              <div style={{ gridColumn: 'span 2', display: 'flex' }}>
                <strong style={{ width: '120px' }}>{language === 'English' ? 'Name :' : 'à¤¨à¤¾à¤µ :'}</strong>
                <span>{student?.studentName || student?.stdName}</span>
              </div>
              
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Std. :' : 'à¤‡à¤¯à¤¤à¥à¤¤à¤¾ :'}</strong><span>{(student?.currentClass || classValue || '').replace('Class ', '')}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Div. :' : 'à¤¤à¥à¤•à¤¡à¥€ :'}</strong><span>{student?.division || division || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Std. :' : 'à¤‡à¤¯à¤¤à¥ à¤¤à¤¾ :'}</strong><span>{(student?.currentClass || classValue || '').replace('Class ', '')}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Div. :' : 'à¤¤à¥ à¤•à¤¡à¥€ :'}</strong><span>{student?.division || division || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Roll No. :' : 'à¤¹à¤œà¥‡à¤°à¥€ à¤•à¥ à¤°. :'}</strong><span>{student?.rollNo || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Blood Gr. :' : 'à¤°à¤•à¥ à¤¤à¤—à¤Ÿ :'}</strong><span>{student?.bloodGroup || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'UDISE No. :' : 'à¤¯à¥‚-à¤¡à¤¾à¤¯à¤¸ à¤•à¥ à¤°. :'}</strong><span>{udiseNumber || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Gen. Regd. No. :' : 'à¤¨à¥‹à¤‚à¤¦à¤£à¥€ à¤•à¥ à¤°. :'}</strong><span>{student?.studentId || student?.registerNo || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Aadhaar No. :' : 'à¤†à¤§à¤¾à¤° à¤•à¥ à¤°. :'}</strong><span>{student?.stdAdharNo || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'DOB :' : 'जन्मतारीख :'}</strong><span>{student?.dob ? student.dob.split('-').reverse().join('-') : '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'PEN No. :' : 'à¤ªà¥‡à¤¨ à¤•à¥ à¤°. :'}</strong><span>{student?.aparId || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'APAR No. ID :' : 'à¤…à¤ªà¤¾à¤° à¤†à¤¯à¤¡à¥€ :'}</strong><span>{student?.aparId || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Mobile No. :' : 'à¤®à¥‹à¤¬à¤¾à¤ˆà¤² à¤•à¥ à¤°. :'}</strong><span>{student?.mobileNo || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Mothers Name :' : 'à¤†à¤ˆà¤šà¥‡ à¤¨à¤¾à¤µ :'}</strong><span>{student?.stdMother || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Weight (kg) :' : 'à¤µà¤œà¤¨ (à¤•à¤¿à¤—à¥ à¤°à¥…) :'}</strong><span>{student?.weightSeptember || student?.weightMarch || '-'}</span></div>
              <div style={{ display: 'flex' }}><strong style={{ width: '100px' }}>{language === 'English' ? 'Height (cm) :' : 'à¤‰à¤‚à¤šà¥€ (à¤¸à¥‡à¤®à¥€) :'}</strong><span>{student?.heightSeptember || student?.heightMarch || '-'}</span></div>
              
              <div style={{ gridColumn: 'span 2', display: 'flex' }}>
                <strong style={{ width: '120px' }}>{language === 'English' ? 'Address :' : 'à¤ªà¤¤à¥à¤¤à¤¾ :'}</strong>
                <span>{student?.address || '-'}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

    <div className="template-result-sheet-container" style={{
      width: '100%',
      maxWidth: '280mm',
      margin: '0 auto',
      backgroundColor: '#fff',
      padding: '10px 12px',
      boxSizing: 'border-box',
      fontFamily: '"Times New Roman", Times, serif',
      fontSize: '11px',
      color: '#000',
      border: '2px solid #000',
      minHeight: '185mm'
    }}>
      <style>{`
        @page {
          size: landscape !important;
          margin: 8mm !important;
        }
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            background: #fff !important;
          }
          .template-result-sheet-container {
            width: 100% !important;
            max-width: 280mm !important;
            margin: 4mm auto !important;
            padding: 4mm 6mm !important;
            box-sizing: border-box !important;
            border: 2px solid #000 !important;
            page-break-inside: avoid !important;
            min-height: 190mm !important;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: '2px solid #000', paddingBottom: '6px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
            Std. {student.currentClass || classValue || 'IX'}
          </div>
          <div style={{ textAlign: 'center', flex: 1, padding: '0 10px' }}>
            <h5 style={{ margin: 0, fontWeight: 'bold', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {schoolName || "A.E.S. ASHOKBHAU FIRODIA ENGLISH MEDIUM SCHOOL & JUNIOR COLLEGE, AHILYANAGAR"}
            </h5>
          </div>
          <div style={{ textAlign: 'right', fontSize: '10px', lineHeight: '1.4' }}>
            <div>Gen. Reg. No. <span style={{ borderBottom: '1px solid #000', minWidth: '50px', display: 'inline-block', textAlign: 'center', fontWeight: 'bold' }}>{student.studentId || student.registerNo || '-'}</span></div>
            <div>Roll No. <span style={{ borderBottom: '1px solid #000', minWidth: '50px', display: 'inline-block', textAlign: 'center', fontWeight: 'bold' }}>{student.rollNo || '-'}</span></div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', flexWrap: 'wrap' }}>
          <div>
            Student's Name : <span style={{ borderBottom: '1px solid #000', fontWeight: 'bold', paddingRight: '10px', minWidth: '150px', display: 'inline-block' }}>{student.studentName || student.stdName}</span>
          </div>
          <div>
            Std. <span style={{ borderBottom: '1px solid #000', fontWeight: 'bold', padding: '0 5px' }}>{student.currentClass || classValue || 'IX'}</span>
            Div. <span style={{ borderBottom: '1px solid #000', fontWeight: 'bold', padding: '0 5px' }}>{student.division || division || '-'}</span>
          </div>
          <div>
            Year <span style={{ borderBottom: '1px solid #000', fontWeight: 'bold', padding: '0 5px' }}>{academicYear || '2025-2026'}</span>
          </div>
          <div>
            Class Teacher's Name : <span style={{ borderBottom: '1px solid #000', fontWeight: 'bold', minWidth: '100px', display: 'inline-block' }}>{student.classTeacher || ''}</span>
          </div>
        </div>
      </div>

      {/* Main Layout: Table on Left + Attendance on Right */}
      <div style={{ display: 'flex', gap: '4px', width: '100%', marginBottom: '8px' }}>
        {/* Dynamic Main Table */}
        <div style={{ flex: 1, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontSize: '10px', textAlign: 'center' }}>
            <thead>
              <tr>
                <th rowSpan={3} style={{ border: '1px solid #000', padding: '3px', verticalAlign: 'middle', width: '110px' }}>SUBJECTS</th>
                <th colSpan={6} style={{ border: '1px solid #000', padding: '3px', textAlign: 'center', verticalAlign: 'middle' }}>I Term</th>
                <th colSpan={6} style={{ border: '1px solid #000', padding: '3px', textAlign: 'center', verticalAlign: 'middle' }}>II Term</th>
                <th rowSpan={3} style={{ border: '1px solid #000', padding: '3px', verticalAlign: 'middle' }}>Total A+B</th>
                <th rowSpan={3} style={{ border: '1px solid #000', padding: '3px', verticalAlign: 'middle' }}>A+B / 2 (100)</th>
                <th rowSpan={3} style={{ border: '1px solid #000', padding: '3px', verticalAlign: 'middle' }}>Grace Marks</th>
                <th rowSpan={3} style={{ border: '1px solid #000', padding: '3px', verticalAlign: 'middle' }}>Total 100</th>
              </tr>
              <tr>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '2px' }}>Internal Marks</th>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '2px' }}>Theory</th>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '2px' }}>Total A</th>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '2px' }}>Internal Marks</th>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '2px' }}>Theory</th>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '2px' }}>Total B</th>
              </tr>
              <tr>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Marks</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Out Of</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Marks</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Out Of</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Marks</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Out Of</th>

                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Marks</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Out Of</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Marks</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Out Of</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Marks</th>
                <th style={{ border: '1px solid #000', padding: '2px', fontSize: '8px' }}>Out Of</th>
              </tr>
            </thead>
            <tbody>
              {computedWrittenRows.map((r, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #000', padding: '3px 4px', textAlign: 'left', fontWeight: 'bold' }}>{r.name}</td>

                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem1_int_m ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem1_int_out || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem1_th_m ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem1_th_out || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem1_tot_m ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem1_tot_out || ''}</td>

                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem2_int_m ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem2_int_out || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem2_th_m ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem2_th_out || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem2_tot_m ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.sem2_tot_out || ''}</td>

                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.tot_AB ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.ab_div_2 ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.grace ?? ''}</td>
                  <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{r.final_100 ?? ''}</td>
                </tr>
              ))}

              {/* Grand Total Row */}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                <td style={{ border: '1px solid #000', padding: '3px 4px', textAlign: 'left' }}>Grand Total</td>

                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem1_int_m || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem1_int_out || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem1_th_m || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem1_th_out || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem1_tot_m || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem1_tot_out || ''}</td>

                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem2_int_m || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem2_int_out || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem2_th_m || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem2_th_out || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem2_tot_m || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_sem2_tot_out || ''}</td>

                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_tot_AB || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_ab_div_2 || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_grace || ''}</td>
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{grand_final_100 || ''}</td>
              </tr>

              {/* Percentage Row */}
              <tr style={{ fontWeight: 'bold' }}>
                <td style={{ border: '1px solid #000', padding: '3px 4px', textAlign: 'left' }}>Percentage</td>
                {Array.from({ length: 15 }).map((_, i) => (
                  <td key={i} style={{ border: '1px solid #000', padding: '3px 2px' }}></td>
                ))}
                <td style={{ border: '1px solid #000', padding: '3px 2px' }}>{overallPercentage ? `${overallPercentage}%` : ''}</td>
              </tr>

            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Section */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
        {/* Left Side Boxes */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {/* Dynamic Grade Subjects Table */}
            <table style={{ borderCollapse: 'collapse', border: '1px solid #000', fontSize: '9px', minWidth: '130px' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #000', padding: '2px' }}>Grade Subjects</th>
                  <th style={{ border: '1px solid #000', padding: '2px' }}>Final Grade</th>
                </tr>
              </thead>
              <tbody>
                {displayGradeSubjects.map((sub, i) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{sub}</td>
                    <td style={{ border: '1px solid #000', padding: '2px', textAlign: 'center', fontWeight: 'bold' }}>{getGradeValueForSubject(sub)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Grade Subject Description Key */}
            <table style={{ borderCollapse: 'collapse', border: '1px solid #000', fontSize: '8px' }}>
              <thead>
                <tr>
                  <th colSpan={2} style={{ border: '1px solid #000', padding: '2px', textAlign: 'center' }}>Grade Subject Description</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>A</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>60% & above</td></tr>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>B</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>45% to 59%</td></tr>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>C</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>35% to 44%</td></tr>
              </tbody>
            </table>

            {/* Percentage Key */}
            <table style={{ borderCollapse: 'collapse', border: '1px solid #000', fontSize: '8px' }}>
              <thead>
                <tr>
                  <th colSpan={2} style={{ border: '1px solid #000', padding: '2px', textAlign: 'center' }}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>75% & above</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>Excellent</td></tr>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>60% - 74%</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>V. Good</td></tr>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>50% - 59%</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>Good</td></tr>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>35% - 49%</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>Satisfactory</td></tr>
                <tr><td style={{ border: '1px solid #000', padding: '1px', textAlign: 'center', fontWeight: 'bold' }}>0% - 34%</td><td style={{ border: '1px solid #000', padding: '1px 3px' }}>Not Satisfactory</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Side Summary Box */}
        <div style={{ width: '300px', border: '1px solid #000', padding: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '10px' }}>
          <div>
            <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
              Passed & Promoted to Std. <span style={{ borderBottom: '1px solid #000', padding: '0 10px', display: 'inline-block' }}>{getPromotedClass(student.currentClass || classValue)}</span> / Detained in Std. <span style={{ borderBottom: '1px solid #000', padding: '0 10px', display: 'inline-block' }}></span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '3px', marginBottom: '6px' }}>
              <div>Percentage :</div>
              <div style={{ fontWeight: 'bold' }}>{overallPercentage}%</div>
              {overallGrade !== 'E' && (
                <>
                  <div>Grade :</div>
                  <div style={{ fontWeight: 'bold' }}>{overallGrade}</div>
                </>
              )}
              <div>Remark :</div>
              <div style={{ fontWeight: 'bold' }}>{getGradeDesc(overallGrade)}</div>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontWeight: 'bold', fontSize: '9px' }}>
              <div>Class Teacher</div>
              <div>Moderator</div>
              <div>Incharge</div>
              <div>Principal</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TemplateResultView;








